import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { Conversation } from '../utils/types';

const LOCAL_STORAGE_KEY = 'technicalSupportChatbot_conversations';

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const [recentConversations, setRecentConversations] = useState<Conversation[]>([]);
    const [clearSuccess, setClearSuccess] = useState(false);
    const [confirmingDeleteId, setConfirmingDeleteId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [confirmingClearStorage, setConfirmingClearStorage] = useState(false);
    const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            if (deleteTimeoutRef.current) {
                clearTimeout(deleteTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const savedConversations = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (savedConversations) {
            try {
                const allConversations: Conversation[] = JSON.parse(savedConversations);
                const sorted = [...allConversations].sort((a, b) => b.id - a.id);
                setRecentConversations(sorted.slice(0, 3));
            } catch (error) {
                console.error('Error parsing conversations from localStorage:', error);
                setRecentConversations([]);
            }
        }
    }, [clearSuccess]);

    const handleClearStorage = () => {
        setConfirmingClearStorage(true);
    };

    const handleConfirmClearStorage = () => {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        setRecentConversations([]);
        setClearSuccess(true);
        setConfirmingClearStorage(false);
        setTimeout(() => setClearSuccess(false), 3000);
    };

    const handleCancelClearStorage = () => {
        setConfirmingClearStorage(false);
    };

    const handleDeleteClick = (id: number, event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setConfirmingDeleteId(id);
    };

    const handleConfirmDelete = (id: number, event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        setDeletingId(id);
        setConfirmingDeleteId(null);

        if (deleteTimeoutRef.current) {
            clearTimeout(deleteTimeoutRef.current);
        }

        deleteTimeoutRef.current = setTimeout(() => {
            const savedConversations = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (savedConversations) {
                try {
                    const allConversations: Conversation[] = JSON.parse(savedConversations);
                    const filteredConversations = allConversations.filter(convo => convo.id !== id);

                    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(filteredConversations));

                    const sorted = [...filteredConversations].sort((a, b) => b.id - a.id);
                    setRecentConversations(sorted.slice(0, 3));

                    setDeletingId(null);
                } catch (error) {
                    console.error('Error deleting conversation:', error);
                }
            }
        }, 300);
    };

    const handleCancelDelete = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setConfirmingDeleteId(null);
    };


    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex flex-col justify-center items-center p-4">
            <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-8">
                    <h1 className="text-3xl font-bold text-blue-800 mb-6">Welcome to Alex AI</h1>

                    <p className="text-gray-600 mb-8">
                        Please select one of our services below to get started:
                    </p>


                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div
                            className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg p-6 text-center transition-colors flex flex-col items-center shadow-md hover:shadow-lg cursor-pointer relative"
                            onClick={() => navigate('/alex')}
                        >
                            <div className="flex items-center mb-2">
                                <h2 className="text-xl font-semibold">Xelacore Data Copilot</h2>
                            </div>
                            <p className="text-sm">
                                Get help from our AI-powered data assistant
                            </p>
                        </div>

                        <Link to="/concept-sort"
                              className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg p-6 text-center transition-colors flex flex-col items-center shadow-md hover:shadow-lg">
                            <h2 className="text-xl font-semibold mb-2">Sort Concept</h2>
                            <p className="text-sm">Ask Alex to sort concepts for each product</p>
                        </Link>

                        <Link to="/match-fighters"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg p-6 text-center transition-colors flex flex-col items-center shadow-md hover:shadow-lg">
                            <h2 className="text-xl font-semibold mb-2">Match Fighters</h2>
                            <p className="text-sm">Ask Alex to match fighters in products</p>
                        </Link>

                        <Link to="/nbc"
                              className="md:col-start-2 bg-[#ddd4bc] hover:bg-[#c0ae7e] text-white rounded-lg p-6 text-center transition-colors flex flex-col items-center shadow-md hover:shadow-lg">
                            <h2 className="text-xl font-semibold mb-2">Smart DPP</h2>
                            <p className="text-sm">Ask Alex about our Digital Product Passport</p>
                        </Link>
                    </div>

                    {recentConversations.length > 0 && (
                        <div className="mt-8">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Quick Access to Recent Conversations</h2>
                            <div className="space-y-3">
                                {recentConversations.map(conversation => (
                                    <div
                                        key={conversation.id}
                                        className={`relative group transition-all duration-300 transform ${
                                            deletingId === conversation.id 
                                                ? 'opacity-0 scale-95 h-0 my-0 py-0 overflow-hidden' 
                                                : 'opacity-100'
                                        }`}
                                    >
                                        <Link
                                            to={`/alex/${conversation.id}`}
                                            className="block p-4 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors"
                                        >
                                            <div className="flex justify-between items-center">
                                                <h3 className="font-medium text-blue-700 truncate">
                                                    {conversation.title.substring(0, 60)}
                                                    {conversation.title.length > 100 ? '...' : ''}
                                                </h3>
                                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                    {conversation.messages.length} messages
                                                </span>
                                            </div>
                                            {conversation.messages.length > 0 && (
                                                <p className="text-sm text-gray-500 mt-1 truncate">
                                                    {conversation.messages[conversation.messages.length - 1].content.substring(0, 100)}
                                                    {conversation.messages[conversation.messages.length - 1].content.length > 100 ? '...' : ''}
                                                </p>
                                            )}
                                        </Link>
                                        {confirmingDeleteId === conversation.id ? (
                                            <div className="absolute top-2 right-2 flex space-x-1 items-center bg-white px-1 py-0.5 rounded shadow-sm">
                                                <button
                                                    onClick={(e) => handleConfirmDelete(conversation.id, e)}
                                                    className="p-1 text-xs font-medium text-white bg-red-500 rounded hover:bg-red-600 transition-colors"
                                                    title="Confirm delete"
                                                >
                                                    Delete
                                                </button>
                                                <button
                                                    onClick={handleCancelDelete}
                                                    className="p-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                                                    title="Cancel"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={(e) => handleDeleteClick(conversation.id, e)}
                                                className="absolute top-2 right-2 p-1 rounded-full bg-red-100 text-red-700 opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-red-200 transform hover:scale-110"
                                                title="Delete conversation"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-gray-50 px-8 py-4 border-t border-gray-200">
                    <div className="flex flex-col md:flex-row justify-center items-center space-y-2 md:space-y-0 md:space-x-4">
                        <p className="text-gray-500 text-sm text-center">
                            © 2025 Xelcore. All rights reserved.
                        </p>
                        {confirmingClearStorage ? (
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={handleConfirmClearStorage}
                                    className="flex items-center text-xs px-3 py-1 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                                    title="Confirm clear storage"
                                >
                                    <Trash2 size={14} className="mr-1" />
                                    <span>Clear</span>
                                </button>
                                <button
                                    onClick={handleCancelClearStorage}
                                    className="flex items-center text-xs px-3 py-1 rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                                    title="Cancel"
                                >
                                    <span>Cancel</span>
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={handleClearStorage}
                                className={`flex items-center text-xs px-3 py-1 rounded-full transition-colors ${
                                    clearSuccess 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                                }`}
                            >
                                {clearSuccess ? (
                                    <span>Storage cleared!</span>
                                ) : (
                                    <>
                                        <Trash2 size={14} className="mr-1" />
                                        <span>Clear storage</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomePage;